import type { AdminInitiativeLead } from '../api/admin';
import { findComponentProjectById } from '../config/componentProjects';

/** Candidate strings for matching API / template subawardee labels. */
export function extractSubawardeeCandidateNames(rawName: string): string[] {
  const trimmed = rawName.trim();
  if (!trimmed) return [];

  const candidates = new Set<string>([trimmed]);
  const paren = trimmed.match(/\(([^)]+)\)\s*$/);
  if (paren?.[1]?.trim()) {
    candidates.add(paren[1].trim());
  }
  const withoutCorporateSuffix = trimmed
    .replace(/,\s*(LLC|Inc\.?|L\.L\.C\.)\s*$/i, '')
    .trim();
  if (withoutCorporateSuffix && withoutCorporateSuffix !== trimmed) {
    candidates.add(withoutCorporateSuffix);
  }
  return Array.from(candidates);
}

function normalizeSubawardeeToken(value: string): string {
  return value.trim().toLowerCase();
}

/** Whether a subawardee label belongs to an initiative lead's portfolio (API data only). */
export function subawardeeMatchesLeadPortfolio(
  subawardee: string,
  leadSubawardees: string[]
): boolean {
  const subTokens = extractSubawardeeCandidateNames(subawardee).map(normalizeSubawardeeToken);
  if (subTokens.length === 0) return false;

  return leadSubawardees.some((leadSub) => {
    const leadTokens = extractSubawardeeCandidateNames(leadSub).map(normalizeSubawardeeToken);
    return subTokens.some((sub) => leadTokens.some((lead) => sub === lead));
  });
}

export function findInitiativeLeadForSubawardee(
  subawardee: string,
  leads: AdminInitiativeLead[]
): AdminInitiativeLead | undefined {
  return leads.find((lead) => subawardeeMatchesLeadPortfolio(subawardee, lead.subawardees));
}

/**
 * Display name from the admin initiative-leads API only.
 * Uses the row's stored initiative_lead when it matches a directory entry by id or name.
 * Does not infer leads from subawardee ownership (avoids stale portfolio mappings).
 */
export function getInitiativeLeadDisplayName(
  initiativeLeadField: string | null | undefined,
  leads: AdminInitiativeLead[]
): string | null {
  const trimmed = initiativeLeadField?.trim();
  if (!trimmed) return null;

  const normalized = trimmed.toLowerCase();
  const byId = leads.find((lead) => lead.id.toLowerCase() === normalized);
  if (byId) return byId.name;

  const byName = leads.find((lead) => lead.name.toLowerCase() === normalized);
  if (byName) return byName.name;

  return null;
}

/** Leads scoped to a component project (navbar slug/id or derived from sub-awardee). */
export function filterLeadsForComponentProject(
  leads: AdminInitiativeLead[],
  componentProjectId: string | null | undefined,
  options: { view: 'component' | 'subawardee'; subawardee: string | null } = {
    view: 'component',
    subawardee: null,
  }
): AdminInitiativeLead[] {
  let projectId = componentProjectId;
  if (!projectId && options.view === 'subawardee' && options.subawardee) {
    const owner = findInitiativeLeadForSubawardee(options.subawardee, leads);
    const projectName = owner?.component_projects[0];
    if (projectName) {
      const project = findComponentProjectById(projectName);
      projectId = project?.id ?? projectName;
    }
  }

  const project = findComponentProjectById(projectId);
  if (!project) return leads;

  return leads.filter((lead) =>
    lead.component_projects.some(
      (name) =>
        name.toLowerCase() === project.name.toLowerCase() ||
        name.toLowerCase() === project.id.toLowerCase()
    )
  );
}

export function findInitiativeLead(
  leads: AdminInitiativeLead[],
  leadId: string | null | undefined
): AdminInitiativeLead | undefined {
  if (!leadId) return undefined;
  return leads.find((lead) => lead.id === leadId);
}
